// src/hooks/usePortfolio.js - FIXED
import { useState, useCallback } from 'react';
import { portfolioService } from '../services/portfolioService';

export const usePortfolio = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recentDeposits, setRecentDeposits] = useState([]);
  const [recentWithdrawals, setRecentWithdrawals] = useState([]);
  const [referralInfo, setReferralInfo] = useState({
    referralCode: '',
    referralLink: '',
    totalReferrals: 0
  });
  const [referralBonusSummary, setReferralBonusSummary] = useState(null);
  const [referralWallet, setReferralWallet] = useState(null);
  const [portfolioSummary, setPortfolioSummary] = useState(null);
  const [walletBalance, setWalletBalance] = useState(null);

  // ✅ FIXED: Wrap in useCallback to prevent recreation
  const fetchRecentTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [depositsData, withdrawalsData] = await Promise.all([
        portfolioService.getRecentDeposits(3),
        portfolioService.getRecentWithdrawals(3)
      ]);
      
      const deposits = depositsData.results || depositsData;
      const withdrawals = withdrawalsData.results || withdrawalsData;
      
      setRecentDeposits(
        deposits.filter(t => t.transaction_type === 'deposit').slice(0, 3)
      );
      setRecentWithdrawals(
        withdrawals.filter(t => t.transaction_type === 'withdrawal').slice(0, 3)
      );
    } catch (err) {
      setError(err.message || 'Failed to fetch transactions');
      console.error('Error fetching recent transactions:', err);
    } finally {
      setLoading(false);
    }
  }, []); // Empty deps - function never changes

  const fetchReferralInfo = useCallback(async () => {
    try {
      setError(null);
      const data = await portfolioService.getReferralInfo();
      setReferralInfo({
        referralCode: data.referral_code || '',
        referralLink: data.referral_link || '',
        totalReferrals: data.total_referrals || 0
      });
    } catch (err) {
      setError(err.message || 'Failed to fetch referral info');
      console.error('Error fetching referral info:', err);
      setReferralInfo({
        referralCode: '',
        referralLink: '',
        totalReferrals: 0
      });
    }
  }, []);

  const fetchReferralBonusSummary = useCallback(async () => {
    try {
      setError(null);
      const data = await portfolioService.getReferralBonusSummary();
      setReferralBonusSummary(data);
    } catch (err) {
      setError(err.message || 'Failed to fetch referral bonus summary');
      console.error('Error fetching referral bonus summary:', err);
    }
  }, []);

  const fetchReferralWallet = useCallback(async () => {
    try {
      setError(null);
      const data = await portfolioService.getReferralWallet();
      setReferralWallet(data);
    } catch (err) {
      setError(err.message || 'Failed to fetch referral wallet');
      console.error('Error fetching referral wallet:', err);
    }
  }, []);

  const fetchPortfolioSummary = useCallback(async () => {
    try {
      setError(null);
      const data = await portfolioService.getPortfolioSummary();
      setPortfolioSummary(data);
    } catch (err) {
      setError(err.message || 'Failed to fetch portfolio summary');
      console.error('Error fetching portfolio summary:', err);
    }
  }, []);

  const fetchWalletBalance = useCallback(async () => {
    try {
      setError(null);
      const data = await portfolioService.getWalletBalance();
      setWalletBalance(data);
    } catch (err) {
      setError(err.message || 'Failed to fetch wallet balance');
      console.error('Error fetching wallet balance:', err);
    }
  }, []);

  const copyReferralCode = useCallback(() => {
    if (referralInfo.referralCode) {
      navigator.clipboard.writeText(referralInfo.referralCode);
      return true;
    }
    return false;
  }, [referralInfo.referralCode]);

  const copyReferralLink = useCallback(() => {
    if (referralInfo.referralLink) {
      navigator.clipboard.writeText(referralInfo.referralLink);
      return true;
    }
    return false;
  }, [referralInfo.referralLink]);

  const refreshPortfolio = useCallback(async () => {
    await Promise.all([
      fetchRecentTransactions(),
      fetchReferralInfo(),
      fetchReferralBonusSummary(),
      fetchReferralWallet(),
      fetchPortfolioSummary(),
      fetchWalletBalance()
    ]);
  }, [
    fetchRecentTransactions,
    fetchReferralInfo,
    fetchReferralBonusSummary,
    fetchReferralWallet,
    fetchPortfolioSummary,
    fetchWalletBalance
  ]);

  return {
    loading,
    error,
    recentDeposits,
    recentWithdrawals,
    referralInfo,
    referralBonusSummary,
    referralWallet,
    portfolioSummary,
    walletBalance,
    fetchRecentTransactions,
    fetchReferralInfo,
    fetchReferralBonusSummary,
    fetchReferralWallet,
    fetchPortfolioSummary,
    fetchWalletBalance,
    copyReferralCode,
    copyReferralLink,
    refreshPortfolio
  };
};